import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeader1 from '../../components/common/PageHeader1';

const API_URL = `${process.env.REACT_APP_BASEURL}/admin/cashback`;
const GET_URL = `${process.env.REACT_APP_BASEURL}/admin/cashBack`;
const AUTH_TOKEN = localStorage.getItem('astroVani-admin-token');

const sessionTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'chat', label: 'Chat' },
    { value: 'call', label: 'Call' },
    { value: 'videoCall', label: 'Video Call' },
];

const typeOptions = [
    { value: 'fixed', label: 'Fixed' },
    { value: 'percentage', label: 'Percentage' },
];

const formatTimeForInput = (time) => {
    if (!time || typeof time !== 'string') return '';

    const [hours = '', minutes = ''] = time.split(':');

    if (hours === '' || minutes === '') return '';

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const formatTimeForApi = (time) => {
    if (!time || typeof time !== 'string') return undefined;

    const [hours = '', minutes = ''] = time.split(':');

    if (hours === '' || minutes === '') return undefined;

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const getCurrentDateString = () => dayjs().format('YYYY-MM-DD');

const getCurrentTimeString = () => dayjs().format('HH:mm');

const getLaterTime = (firstTime, secondTime) => {
    if (!firstTime) return secondTime;
    if (!secondTime) return firstTime;

    return firstTime > secondTime ? firstTime : secondTime;
};

const isStartDateTimeInPast = (validFrom, startTime) => {
    if (!validFrom || !startTime) return false;

    const normalizedStartTime = formatTimeForApi(startTime);

    if (!normalizedStartTime) return true;

    const startDateTime = new Date(`${validFrom}T${normalizedStartTime}`);

    if (Number.isNaN(startDateTime.getTime())) {
        return true;
    }

    return startDateTime < new Date();
};

const isStartDateTimeBeforeEndDateTime = (validFrom, startTime, validTo, endTime) => {
    if (!validFrom || !startTime || !validTo || !endTime) return true;

    const normalizedStartTime = formatTimeForApi(startTime);
    const normalizedEndTime = formatTimeForApi(endTime);

    if (!normalizedStartTime || !normalizedEndTime) return false;

    const startDateTime = new Date(`${validFrom}T${normalizedStartTime}`);
    const endDateTime = new Date(`${validTo}T${normalizedEndTime}`);

    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        return false;
    }

    return startDateTime < endDateTime;
};

function CashBackAdd() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        type: 'fixed',
        value: '',
        validFrom: '',
        validTo: '',
        startTime: '',
        endTime: '',
        sessionType: 'all',
        maxUsesPerUser: 1,
        isActive: true,
        isFreeChat: false,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // 🔥 NEW STATES
    const [openConfirm, setOpenConfirm] = useState(false);
    const [pendingBody, setPendingBody] = useState(null);

    const today = getCurrentDateString();
    const nowTime = getCurrentTimeString();
    const isSameDateRange =
        form.validFrom &&
        form.validTo &&
        form.validFrom === form.validTo;
    const isStartDateToday = form.validFrom === today;
    const isEndDateToday = form.validTo === today;
    const startTimeMin = isStartDateToday ? nowTime : undefined;
    const endTimeMin = getLaterTime(
        isSameDateRange ? form.startTime || undefined : undefined,
        isEndDateToday ? nowTime : undefined
    );

    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            fetch(`${GET_URL}/${id}`, {
                headers: { Authorization: AUTH_TOKEN },
            })
                .then(res => res.json())
                .then(data => {
                    setForm({
                        ...data.data,
                        validFrom: data.data.validFrom
                            ? dayjs(data.data.validFrom).format('YYYY-MM-DD')
                            : '',
                        validTo: data.data.validTo
                            ? dayjs(data.data.validTo).format('YYYY-MM-DD')
                            : '',
                        startTime: data.data.startTime
                            ? formatTimeForInput(data.data.startTime)
                            : '',
                        endTime: data.data.endTime
                            ? formatTimeForInput(data.data.endTime)
                            : '',
                        isFreeChat: data.data.isFreeChat || false,
                    });
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    }, [id, isEdit]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        const currentDate = getCurrentDateString();
        const currentTime = getCurrentTimeString();

        if (name === 'startTime' && form.validFrom === currentDate && value && value < currentTime) {
            setError("Start time cannot be earlier than the current time for today's date");
            setOpenSnackbar(true);
            return;
        }

        if (name === 'endTime' && form.validTo === currentDate && value && value < currentTime) {
            setError("End time cannot be earlier than the current time for today's date");
            setOpenSnackbar(true);
            return;
        }

        setForm(prev => {
            const nextForm = {
                ...prev,
                [name]: nextValue
            };

            if (name === 'validFrom' && nextForm.validTo && nextValue > nextForm.validTo) {
                nextForm.validTo = '';
            }

            if (name === 'validTo' && nextForm.validFrom && nextValue < nextForm.validFrom) {
                nextForm.validFrom = '';
            }

            if (name === 'validFrom' && nextValue === currentDate && nextForm.startTime && nextForm.startTime < currentTime) {
                nextForm.startTime = '';
            }

            if (name === 'validTo' && nextValue === currentDate && nextForm.endTime && nextForm.endTime < currentTime) {
                nextForm.endTime = '';
            }

            const isSameDateRange =
                nextForm.validFrom &&
                nextForm.validTo &&
                nextForm.validFrom === nextForm.validTo;

            if (
                name === 'startTime' &&
                isSameDateRange &&
                nextForm.endTime &&
                nextValue >= nextForm.endTime
            ) {
                nextForm.endTime = '';
            }

            if (
                name === 'endTime' &&
                isSameDateRange &&
                nextForm.startTime &&
                nextValue <= nextForm.startTime
            ) {
                nextForm.startTime = '';
            }

            if (
                (name === 'validFrom' || name === 'validTo') &&
                isSameDateRange &&
                nextForm.startTime &&
                nextForm.endTime &&
                nextForm.startTime >= nextForm.endTime
            ) {
                nextForm.endTime = '';
            }

            return nextForm;
        });
    };

    const handleFreeChatToggle = (checked) => {
        setForm(prev => ({
            ...prev,
            isFreeChat: checked,
            sessionType: checked ? 'chat' : prev.sessionType,
            type: checked ? 'fixed' : prev.type,
        }));
    };

    const createBody = (extra = {}) => ({
        ...form,
        ...extra,
        value: Number(form.value),
        maxUsesPerUser: Number(form.maxUsesPerUser),
        validFrom: form.validFrom
            ? new Date(form.validFrom).toISOString()
            : undefined,
        validTo: form.validTo
            ? new Date(form.validTo).toISOString()
            : undefined,
        startTime: formatTimeForApi(form.startTime),
        endTime: formatTimeForApi(form.endTime),
    });

    const hitApi = async (body) => {
        const method = isEdit ? 'PATCH' : 'POST';
        const url = isEdit ? `${GET_URL}/${id}` : API_URL;

        const { __v, createdAt, updatedAt, _id, ...payload } = body;

        const res = await fetch(url, {
            method,
            headers: {
                Authorization: AUTH_TOKEN,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await res.json();

        return { res, data };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!isStartDateTimeBeforeEndDateTime(form.validFrom, form.startTime, form.validTo, form.endTime)) {
            setError("Start date and time must be before End date and time");
            setOpenSnackbar(true);
            return;
        }

        if (isStartDateTimeInPast(form.validFrom, form.startTime)) {
            setError("Start date and time cannot be earlier than the current date and time");
            setOpenSnackbar(true);
            return;
        }

        setLoading(true);

        const body = createBody();

        try {
            const { res, data } = await hitApi(body);

            setLoading(false);

            // 🔥 HANDLE OVERLAP
            if (data?.data?.confirmOverlap) {
                setPendingBody(body);
                setOpenConfirm(true);
                return;
            }

            if (!res.ok || data.success === false) {
                setError(data.message || "Something went wrong");
                setOpenSnackbar(true);
                return;
            }

            navigate('/cashback-list');
        } catch (err) {
            setLoading(false);
            setError("Network error");
            setOpenSnackbar(true);
        }
    };

    // 🔥 CONFIRM BUTTON CLICK
    const handleConfirm = async () => {
        setOpenConfirm(false);
        setLoading(true);

        try {
            const { res, data } = await hitApi({
                ...pendingBody,
                confirmOverlap: true // ✅ important
            });

            setLoading(false);

            if (!res.ok || data.success === false) {
                setError(data.message || "Something went wrong");
                setOpenSnackbar(true);
                return;
            }

            navigate('/cashback-list');
        } catch (err) {
            setLoading(false);
            setError("Network error");
            setOpenSnackbar(true);
        }
    };

    return (
        <Box>
            <PageHeader1 pagetitle="Add Cash Back" />

            {/* Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>

            {/* 🔥 Confirm Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Overlap</DialogTitle>
                <DialogContent>
                    <Typography>
                        An active cashback already exists for the selected duration.
                        Do you want to continue?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirm}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <form onSubmit={handleSubmit} style={{ marginTop: '10px' }}>
                <Grid container spacing={2} border={1} borderColor="grey.300" p={2} borderRadius={2}>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isFreeChat}
                                    onChange={(e) =>
                                        handleFreeChatToggle(e.target.checked)
                                    }
                                />
                            }
                            label="Free Chat"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={(e) =>
                                        setForm(prev => ({
                                            ...prev,
                                            isActive: e.target.checked
                                        }))
                                    }
                                />
                            }
                            label="Active"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                name="type"
                                value={form.type}
                                label="Type"
                                onChange={handleChange}
                                disabled={form.isFreeChat}
                            >
                                {typeOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Value"
                            name="value"
                            type="number"
                            value={form.value}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Session Type</InputLabel>
                            <Select
                                name="sessionType"
                                value={form.sessionType}
                                label="Session Type"
                                onChange={handleChange}
                                disabled={form.isFreeChat}
                            >
                                {sessionTypeOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Max Uses Per User"
                            name="maxUsesPerUser"
                            type="number"
                            value={form.maxUsesPerUser}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Valid From"
                            name="validFrom"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.validFrom}
                            onChange={handleChange}
                            inputProps={{
                                min: today,
                                max: form.validTo || undefined,
                            }}
                            helperText="Start date cannot be earlier than today or later than end date."
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Start Time"
                            name="startTime"
                            type="time"
                            value={form.startTime}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: startTimeMin,
                                max:
                                    isSameDateRange &&
                                    form.endTime
                                        ? form.endTime
                                        : undefined,
                            }}
                            helperText="Start date and time must be later than the current date and time and earlier than end date and time."
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Valid To"
                            name="validTo"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.validTo}
                            onChange={handleChange}
                            inputProps={{
                                min: form.validFrom || today,
                            }}
                            disabled={!form.validFrom}
                            helperText="End date cannot be earlier than start date."
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="End Time"
                            name="endTime"
                            type="time"
                            value={form.endTime}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: endTimeMin,
                            }}
                            disabled={!form.startTime}
                            helperText="End time cannot be earlier than the current time. If the dates are the same, it must also be later than start time."
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={2} display="flex" justifyContent="end" gap={2}>
                            <Button variant="contained" type="submit" disabled={loading}>
                                {isEdit ? 'Update' : 'Add'} Cash Back
                            </Button>
                            <Button variant="outlined" onClick={() => navigate('/cashback-list')}>
                                Cancel
                            </Button>
                        </Box>
                    </Grid>

                </Grid>
            </form>
        </Box>
    );
}

export default CashBackAdd;
